import { useMemo } from 'react';
import {
  DEFAULT_EMBLEM,
  EMBLEM_COLORS,
  EMBLEM_ICONS,
  EMBLEM_SHAPES,
  type Emblem as EmblemData,
  type EmblemColor,
  type EmblemIcon,
  type EmblemShape,
  randomEmblem,
} from '@sfco/shared';
import { Emblem } from './Emblem.js';
import { cn } from './cn.js';

export interface EmblemDesignerProps {
  value: EmblemData;
  onChange: (next: EmblemData) => void;
  /** Seed used for the "Randomize" button. Re-mounting changes it. */
  randomSeed?: string;
}

/**
 * Three-column picker (Shape | Palette | Icon) with a live 128px preview.
 *
 * Used at signup (auto-populated with three deterministic-random suggestions
 * derived from the entered corp name) and from /settings to re-style.
 */
export function EmblemDesigner({ value, onChange, randomSeed }: EmblemDesignerProps) {
  const suggestions = useMemo(() => {
    const s = randomSeed ?? 'default';
    return [
      randomEmblem(`${s}-1`),
      randomEmblem(`${s}-2`),
      randomEmblem(`${s}-3`),
    ];
  }, [randomSeed]);

  return (
    <div className="space-y-3">
      {/* ── Preview + suggestions ──────────────────────────────────── */}
      <div className="flex items-center gap-4 rounded-lg border border-line bg-panel-3 px-4 py-3">
        <div className="flex flex-col items-center gap-1">
          <Emblem data={value} size={96} />
          <span className="stencil text-[8.5px] tracking-capsWide text-muted">Preview</span>
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="stencil text-[9px] tracking-capsWide text-muted">
            Suggestions
          </p>
          <div className="flex gap-2">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChange(s)}
                className="rounded-md border border-line hover:border-accent/60 p-1 bg-panel transition-colors"
                aria-label={`Use suggestion ${i + 1}`}
              >
                <Emblem data={s} size={40} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => onChange(DEFAULT_EMBLEM)}
              className="stencil text-[9px] tracking-capsWide rounded-md border border-line hover:border-accent/60 text-fg-2 hover:text-accent px-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Pickers ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2">
        <Column label="Shape">
          {EMBLEM_SHAPES.map((shape) => (
            <Swatch
              key={shape}
              active={value.shape === shape}
              onClick={() => onChange({ ...value, shape })}
              label={shape}
            >
              <Emblem
                data={{ ...value, shape }}
                size={36}
              />
            </Swatch>
          ))}
        </Column>

        <Column label="Palette">
          <div className="grid grid-cols-2 gap-1.5">
            <PaletteGroup
              title="Primary"
              value={value.primary}
              onChange={(c: EmblemColor) => onChange({ ...value, primary: c })}
            />
            <PaletteGroup
              title="Accent"
              value={value.accent}
              onChange={(c: EmblemColor) => onChange({ ...value, accent: c })}
            />
          </div>
        </Column>

        <Column label="Icon">
          {EMBLEM_ICONS.map((icon) => (
            <Swatch
              key={icon}
              active={value.icon === icon}
              onClick={() => onChange({ ...value, icon })}
              label={icon}
            >
              <Emblem
                data={{ ...value, icon }}
                size={36}
              />
            </Swatch>
          ))}
        </Column>
      </div>
    </div>
  );
}

function Column({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-line bg-panel-2/40 px-2 py-2 space-y-1.5">
      <p className="stencil text-[9px] tracking-capsWide text-muted border-b border-line pb-1">
        {label}
      </p>
      <div className="grid grid-cols-3 gap-1.5 max-h-[180px] overflow-y-auto sidebar-scroll pr-1">
        {children}
      </div>
    </div>
  );
}

function Swatch({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        'aspect-square flex items-center justify-center rounded-md border p-0.5 bg-panel transition-colors',
        active
          ? 'border-accent'
          : 'border-line hover:border-line-strong',
      )}
    >
      {children}
    </button>
  );
}

function PaletteGroup({
  title,
  value,
  onChange,
}: {
  title: string;
  value: EmblemColor;
  onChange: (c: EmblemColor) => void;
}) {
  return (
    <div className="col-span-2 space-y-1">
      <p className="stencil text-[8.5px] tracking-capsWide text-muted">{title}</p>
      <div className="grid grid-cols-7 gap-1">
        {EMBLEM_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            title={c}
            className={cn(
              'aspect-square rounded border',
              value === c
                ? 'border-accent ring-1 ring-accent/40'
                : 'border-line hover:border-line-strong',
            )}
            style={{ background: TAILWIND_HEX[c] }}
          />
        ))}
      </div>
    </div>
  );
}

// Matches Emblem.tsx palette — the one place both files agree.
const TAILWIND_HEX: Record<EmblemColor, string> = {
  brass: '#8A6D2F',
  'crt-amber': '#F5A623',
  'crt-red': '#E55A4F',
  'crt-blue': '#6FB8D4',
  'crt-green': '#8FE36B',
  ink: '#E8D8A8',
  paperDeep: '#12140E',
};

// Explicit exported aliases for TS completeness — some build configs fail on
// unused type re-imports, and we want the user of this component to be able
// to import the Emblem shape alongside the designer.
export type { EmblemShape, EmblemIcon, EmblemColor };
